import React from 'react';
import DOMPurify from 'dompurify';
import { Dropdown } from 'semantic-ui-react';
import FormField from 'components/form-field';
import { IPolicyProvider, PolicyArrangementType } from 'types';
import styles from './styles/Q4EPolicy.module.scss';
import { INCOME_FREQUENCY_OPTIONS } from 'constants/options';
import { INSURANCE_TYPES_ICONS_SVG_V2 } from 'constants/insurance';
import { getProviderImgUrl, getMultiplierText } from './utils';

const FreePolicy = ({
  policy,
  provider,
  values,
  onInputChange,
  onDropdownChange,
}) => {
  const {
    note,
    showIncomeFrequency,
    multiplier,
    policyTypeName,
    hideInput,
    benefit,
  } = policy;
  const Icon = INSURANCE_TYPES_ICONS_SVG_V2[policyTypeName];
  return (
    <div
      className={classnames(
        styles.policy,
        !hideInput && showIncomeFrequency && styles.fullWidth,
      )}
    >
      <img
        className={styles.logo}
        alt={provider.name}
        src={getProviderImgUrl(provider.name)}
      />
      <div className={styles.policyTitle}>
        <Icon /> {`${policyTypeName} insurance`}
      </div>
      <div className={styles.policyFields}>
        {!hideInput && (
          <FormField
            name="income"
            className={styles.policyField}
            labelClassName={styles.policyLabel}
            inputClassName={styles.policyInput}
            value={values.income}
            onChange={onInputChange}
            placeholder={`Enter your ${
              showIncomeFrequency ? '' : 'annual '
            }salary`}
            prefix="$"
            inputType="number"
            decimalScale={0}
          />
        )}
        {showIncomeFrequency && (
          <Dropdown
            name="incomeFrequency"
            placeholder="Select pay frequency"
            selection={true}
            options={INCOME_FREQUENCY_OPTIONS}
            className={styles.policyDropdown}
            icon="chevron down"
            onChange={onDropdownChange}
            value={values.incomeFrequency}
          />
        )}
        {benefit && (
          <div className={styles.benefit}>
            <h3>{benefit}</h3>
            <span className={styles.benefitLabel}>BENEFIT</span>
          </div>
        )}
      </div>
      {note !== false && (
        <div className={styles.note}>
          <span>*</span>
          {note ? (
            <span
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note) }}
            />
          ) : (
            `Your free ${policyTypeName} insurance cover is for ${getMultiplierText(
              multiplier,
            )} your yearly salary`
          )}
        </div>
      )}
    </div>
  );
};

const DiscountPolicy = ({ policy, provider }) => {
  const { policyTypeName, note } = policy;
  const { name } = provider;
  return (
    <div className={styles.policy}>
      <img className={styles.logo} alt={provider.name} src={provider.logoUrl} />
      <div className={styles.bold}>{`${policyTypeName} insurance`}</div>
      <div>{`Get discounted ${name} ${policyTypeName.toLowerCase()} insurance.`}</div>
      {note && <div>{note}</div>}
    </div>
  );
};

const PolicyComponentMap = {
  [PolicyArrangementType.DISCOUNT]: DiscountPolicy,
  [PolicyArrangementType.FREE]: FreePolicy,
};

interface IProps {
  policy;
  provider: IPolicyProvider;
  values: any;
  onInputChange(e): void;
  onDropdownChange(_, data): void;
}

const Q4EPolicy = (props: IProps) => {
  if (
    !props.provider ||
    props.policy.arrangementType === PolicyArrangementType.DISCOUNT
  ) {
    return null;
  }

  const Component = PolicyComponentMap[props.policy.arrangementType];
  return Component && <Component {...props} />;
};

export default Q4EPolicy;
